// combatpro/monster/monsterSelectionEngine.js
//
// Combat PRO - Encounter Monster Selection engine (Stage 61: Encounter Monster
// Selection Runtime Migration).
//
// The pure, framework-independent core of the encounter monster-selection runtime,
// migrated verbatim in behaviour from the legacy battletracker.html (the read-only
// master spec). It owns monster lookup, the combined official+custom pool, and the
// complete slot-construction logic that the legacy selectMonster performed - the
// data half only. It is the single authoritative builder every future encounter
// insertion uses.
//
// Migrated functions:
//   getAllMonsters     <- getAllMonsters (battletracker.html 5078): concatenate the
//                         official DB and the runtime custom pool, drop entries with
//                         no name, and sort alphabetically by name (localeCompare).
//   findMonster        <- the selectMonster lookup head (6673-6675): find by exact
//                         name in the combined pool.
//   parseHpAverage     <- the selectMonster HP auto-calc (6693-6696): the leading
//                         integer of an hpRange string like "45 (6d10 + 12)" -> 45.
//   mapSelectedAbilities <- the selectMonster ability coercion (6712-6727): objects
//                         pass through; strings resolve against ABILITIES_DB or fall
//                         back to a custom legacy-ability stub; anything else drops.
//   buildSelectedSlot  <- the selectMonster slot writes (6677-6760): produce a new
//                         encounter slot from an existing base slot and a chosen DB /
//                         custom monster, populating EVERY field selectMonster sets.
//
// What is intentionally NOT here (DOM / side effects that React renders
// declaratively, faithful behaviour preserved by state alone): the
// document.getElementById value writes, refreshAbilityScores / refreshAbilities /
// refreshWeapons / refreshLegendaryActions, updatePanel / updateTabName /
// updateEncounterSummary, vi_rebuildSpellDropdownsForMonster, and saveState
// (localStorage, never migrated). Those are the runtime layer's concern (a version
// bump) and the panels render from state.monsters.
//
// buildSelectedSlot is immutable: it never mutates the base slot or the source
// monster; it returns a fresh slot with the selectMonster fields overlaid and every
// other base-slot field preserved (so the result is a COMPLETE slot, never a partial
// one). The HP auto-calc only fires when the slot has no HP yet (!maxHP || 0),
// matching the legacy guard so an existing HP is never clobbered.
//
// NOTE (documented for Stage 62, not changed here): the encounter slot and the
// official MONSTERS_DB use lowercase ability keys { str, dex, ... } and lowercase
// save keys; the Monster Studio model / studioToSelectable emit UPPERCASE
// { STR, DEX, ... } and an empty saves {}. selectMonster copies abilityScores / saves
// verbatim, so a studio-origin selectable fed through here (Stage 62) may not
// populate the lowercase ability panel. This engine reproduces the legacy copy
// exactly; reconciling the casing is the Stage 62 bridge's responsibility.
//
// No React, no JSX, no hooks, no context, no DOM, no querySelector, no innerHTML,
// no fetch, no localStorage, no globals. No optional chaining. No em dash.

// ---- Lookup + combined pool ------------------------------------------------

// getAllMonsters (battletracker.html 5078). Official first, then custom; drop any
// nameless entry; sort by name. Inputs are supplied by the caller (the runtime hook
// reads window.VI_MONSTERS_DB and the provider's custom pool) so this stays pure.
export function getAllMonsters(officialDb, customPool) {
  var official = Array.isArray(officialDb) ? officialDb : [];
  var custom = Array.isArray(customPool) ? customPool : [];
  var all = official.concat(custom).filter(function(m) { return m && m.name; });
  return all.sort(function(a, b) { return String(a.name).localeCompare(String(b.name)); });
}

// The selectMonster lookup head: exact name match in the combined pool.
export function findMonster(name, officialDb, customPool) {
  var all = getAllMonsters(officialDb, customPool);
  for (var i = 0; i < all.length; i++) {
    if (all[i].name === name) { return all[i]; }
  }
  return null;
}

// ---- Helpers ---------------------------------------------------------------

// Leading-integer average from an hpRange string (selectMonster 6693-6696). Returns
// null when no leading integer is present (the legacy did nothing in that case).
export function parseHpAverage(hpRange) {
  if (!hpRange) { return null; }
  var match = String(hpRange).match(/^(\d+)/);
  if (!match) { return null; }
  return parseInt(match[1], 10);
}

// Ability coercion (selectMonster 6712-6727). Objects with a name pass through;
// strings resolve against abilitiesDb, falling back to a custom legacy stub; any
// other shape is dropped (matching the legacy map, which returned the raw value -
// here a non-string, non-named value is filtered out to keep the slot well-formed).
export function mapSelectedAbilities(arr, abilitiesDb) {
  if (!Array.isArray(arr)) { return []; }
  var db = Array.isArray(abilitiesDb) ? abilitiesDb : [];
  var out = [];
  for (var i = 0; i < arr.length; i++) {
    var ability = arr[i];
    if (ability && typeof ability === 'object' && ability.name) {
      out.push(ability);
    } else if (typeof ability === 'string') {
      var found = null;
      for (var j = 0; j < db.length; j++) {
        if (db[j] && db[j].name === ability) { found = db[j]; break; }
      }
      out.push(found ? found : { name: ability, desc: 'Legacy ability - no description available.', custom: true });
    }
  }
  return out;
}

// Overlay the up-to-three auto-populated spell names onto the slot's existing spell
// slots (selectMonster 6737-6745). Preserves the existing slot objects and only
// fills name + castRound for the first min(3, names.length); missing slots are
// created as { name, castRound } to stay well-formed.
function overlaySpells(baseSpells, spellNames) {
  var slots = Array.isArray(baseSpells) ? baseSpells.map(function(s) { return Object.assign({}, s); }) : [];
  if (Array.isArray(spellNames) && spellNames.length > 0) {
    for (var i = 0; i < 3 && i < spellNames.length; i++) {
      if (!slots[i]) { slots[i] = { name: '', castRound: 1 }; }
      slots[i].name = spellNames[i];
      slots[i].castRound = 1;
    }
  }
  return slots;
}

// ---- Slot builder (the data half of selectMonster) -------------------------

// Build a complete encounter slot from an existing base slot and a chosen monster
// (selectMonster 6677-6760). Pure + immutable: the returned slot is a clone of
// baseSlot with every selectMonster field overlaid; all other base-slot fields are
// preserved so the result is always a complete, encounter-ready slot.
export function buildSelectedSlot(baseSlot, monster, abilitiesDb) {
  var base = baseSlot || {};
  var m = monster || {};
  var slot = Object.assign({}, base);

  // Basic fields (6678-6691).
  slot.selectedMonster = m.name;
  slot.customName = m.name;
  slot.size = m.size || 'Medium';
  slot.race = m.race;
  slot.class = m.class;
  slot.language = m.language;
  slot.ac = m.ac;
  slot.passivePerception = m.passivePerception;
  slot.spellDC = m.spellDC || 0;
  slot.hpRange = m.hpRange;
  slot.cr = m.cr;
  slot.saves = Object.assign({}, m.saves);
  slot.immunities = m.immunities || '';
  slot.resistances = m.resistances || '';

  // HP auto-calc, only when the slot has no HP yet (6693-6707, minus the DOM writes).
  if (m.hpRange && (!slot.maxHP || slot.maxHP === 0)) {
    var avg = parseHpAverage(m.hpRange);
    if (avg != null) {
      slot.maxHP = avg;
      slot.currentHP = avg;
    }
  }

  // Ability scores (6710-6712): copy verbatim when present.
  if (m.abilityScores) {
    slot.abilityScores = Object.assign({}, m.abilityScores);
  }

  // Abilities / weapons / legendary actions (6714-6735).
  slot.abilities = mapSelectedAbilities(m.abilities, abilitiesDb);
  slot.weapons = (Array.isArray(m.weapons)) ? m.weapons.map(function(w) { return Object.assign({}, w); }) : [];
  slot.legendaryActions = (Array.isArray(m.legendaryActions)) ? m.legendaryActions.map(function(la) { return Object.assign({}, la); }) : [];

  // Spells: auto-populate the first three slots (6737-6745).
  slot.spells = overlaySpells(base.spells, m.spells);

  // Speed + notes (6748-6749).
  slot.speed = m.speed || '30 ft';
  slot.notes = m.notes || '';

  return slot;
}
